import type { Coordinate, MultiPolygonCoordinates } from '../models/editor-feature';
import { distanceBetween } from './measurement';

/**
 * Turning a drawing gesture into geometry.
 *
 * Pure, and separate from the mutations that put the result into the document, because the
 * shapes are worth getting right on their own terms: a ring that is not closed, or one with
 * two vertices, is invalid GeoJSON that renders as nothing and saves without complaint.
 */

/** A ring with its first vertex repeated at the end, which is what closes a polygon. */
export const closeRing = (points: readonly Coordinate[]): Coordinate[] => {
  if (points.length === 0) {
    return [];
  }

  const [first] = points;
  const last = points[points.length - 1];

  return first[0] === last[0] && first[1] === last[1]
    ? [...points]
    : [...points, [first[0], first[1]]];
};

/**
 * Whether a run of points can become a polygon at all.
 *
 * Three distinct vertices is the minimum that encloses anything. Two produce a line with no
 * area, which every renderer draws as nothing — an admin who double-clicks by accident should
 * be told the shape was not made, not left looking for an invisible one.
 */
export const canCloseRing = (points: readonly Coordinate[]): boolean => points.length >= 3;

export const ringToMultiPolygon = (points: readonly Coordinate[]): MultiPolygonCoordinates => (
  [[closeRing(points)]]
);

/**
 * The rectangle two opposite corners describe.
 *
 * Wound anticlockwise in the plan's own Y-up space, matching how the stored data winds its
 * outer rings — the sign of a ring's area is what tells an outer ring from a hole, so a
 * rectangle wound the other way is a hole that happens to have nothing around it.
 */
export const rectangleBetween = (from: Coordinate, to: Coordinate): MultiPolygonCoordinates => {
  const minX = Math.min(from[0], to[0]);
  const maxX = Math.max(from[0], to[0]);
  const minY = Math.min(from[1], to[1]);
  const maxY = Math.max(from[1], to[1]);

  return [[[
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
    [minX, minY],
  ]]];
};

/**
 * How many straight edges an ellipse is drawn with.
 *
 * GeoJSON has no circle: every client in this system — the portal's canvas, the app's
 * vector_map, the backend's validation — understands polygons and nothing else, so a round
 * floor object has to be one. Forty-eight segments is under half a degree of error at the size
 * a pillar or a round table is drawn at, which is finer than the line it is drawn with, while
 * staying small enough that a plan with a hundred of them is still a reasonable document.
 */
export const ELLIPSE_SEGMENTS = 48;

/**
 * An ellipse inscribed in the box between two opposite corners.
 *
 * Cornered like a rectangle rather than dragged from its centre, so the same gesture draws both
 * and a circle can be fitted against a wall without arithmetic. A square box gives a circle,
 * which is what most round floor objects are.
 */
export const ellipseBetween = (
  from: Coordinate,
  to: Coordinate,
  segments: number = ELLIPSE_SEGMENTS,
): MultiPolygonCoordinates => {
  const centreX = (from[0] + to[0]) / 2;
  const centreY = (from[1] + to[1]) / 2;
  const radiusX = Math.abs(to[0] - from[0]) / 2;
  const radiusY = Math.abs(to[1] - from[1]) / 2;
  const edges = Math.max(3, Math.round(segments));

  const ring: Coordinate[] = [];

  for (let step = 0; step < edges; step += 1) {
    const angle = (step / edges) * Math.PI * 2;
    ring.push([centreX + (radiusX * Math.cos(angle)), centreY + (radiusY * Math.sin(angle))]);
  }

  // Closed explicitly rather than by the loop running one step further, so the last point is
  // the first one exactly and not the same angle recomputed to a different float.
  ring.push([ring[0][0], ring[0][1]]);

  return [[ring]];
};

/**
 * The far corner of the square whose corner is `from` and which reaches towards `to`.
 *
 * The longer of the two sides wins, so the square follows the pointer along whichever axis it
 * has travelled furthest — which is what makes a sloppy diagonal still read as "this big".
 */
export const squareCornerFrom = (from: Coordinate, to: Coordinate): Coordinate => {
  const side = Math.max(Math.abs(to[0] - from[0]), Math.abs(to[1] - from[1]));

  return [
    from[0] + (to[0] >= from[0] ? side : -side),
    from[1] + (to[1] >= from[1] ? side : -side),
  ];
};

/** A square with one corner at `from`, sized by the pointer. */
export const squareBetween = (from: Coordinate, to: Coordinate): MultiPolygonCoordinates => (
  rectangleBetween(from, squareCornerFrom(from, to))
);

/** A circle inscribed in that same square. */
export const circleBetween = (from: Coordinate, to: Coordinate): MultiPolygonCoordinates => (
  ellipseBetween(from, squareCornerFrom(from, to))
);

/**
 * A triangle standing in the box between two corners: flat along the bottom, apex at the top.
 *
 * "Top" as the admin sees it, which in the plan's own Y-up space is the larger Y. Wound
 * anticlockwise like every other outer ring the editor draws.
 */
export const triangleBetween = (from: Coordinate, to: Coordinate): MultiPolygonCoordinates => {
  const minX = Math.min(from[0], to[0]);
  const maxX = Math.max(from[0], to[0]);
  const minY = Math.min(from[1], to[1]);
  const maxY = Math.max(from[1], to[1]);

  return [[[
    [minX, minY],
    [maxX, minY],
    [(minX + maxX) / 2, maxY],
    [minX, minY],
  ]]];
};

/** The closed shapes the two-click gesture can draw. A line is the one that is not closed. */
export type BasicShape = 'rectangle' | 'ellipse' | 'square' | 'circle' | 'triangle';

/**
 * The shape two clicks describe, or null when they enclose nothing.
 *
 * One entry point for the drawing hook and the preview alike, so what is previewed while the
 * pointer moves is exactly what lands on the plan when it is clicked.
 */
export const shapeBetween = (
  shape: BasicShape,
  from: Coordinate,
  to: Coordinate,
): MultiPolygonCoordinates | null => {
  if (shape === 'square' || shape === 'circle') {
    const corner = squareCornerFrom(from, to);

    if (corner[0] === from[0]) {
      return null;
    }

    return shape === 'square' ? rectangleBetween(from, corner) : ellipseBetween(from, corner);
  }

  if (from[0] === to[0] || from[1] === to[1]) {
    return null;
  }

  if (shape === 'ellipse') {
    return ellipseBetween(from, to);
  }

  return shape === 'triangle' ? triangleBetween(from, to) : rectangleBetween(from, to);
};

/** Whether a drag produced a rectangle with any area at all. */
export const isDegenerateRectangle = (from: Coordinate, to: Coordinate): boolean => (
  from[0] === to[0] || from[1] === to[1]
);

/**
 * The rectangle a centreline of a given thickness sweeps out.
 *
 * This is how a wall, a door or a window is drawn: as a line with a thickness, because that
 * is how somebody thinks about a wall, and stored as an ordinary polygon, because that is the
 * only geometry the mobile app's floor layer can carry. The centreline and thickness are kept
 * on the feature so the thickness stays editable afterwards — without them, changing a wall
 * from 200mm to 300mm means deleting it and drawing it again.
 */
export const centrelineToRing = (
  from: Coordinate,
  to: Coordinate,
  thicknessUnits: number,
): MultiPolygonCoordinates | null => {
  const length = distanceBetween(from, to);

  if (length <= 0 || !Number.isFinite(thicknessUnits) || thicknessUnits <= 0) {
    return null;
  }

  // The unit normal to the run, which is the direction the thickness is laid off in.
  const normalX = -((to[1] - from[1]) / length) * (thicknessUnits / 2);
  const normalY = ((to[0] - from[0]) / length) * (thicknessUnits / 2);

  return [[[
    [from[0] + normalX, from[1] + normalY],
    [to[0] + normalX, to[1] + normalY],
    [to[0] - normalX, to[1] - normalY],
    [from[0] - normalX, from[1] - normalY],
    [from[0] + normalX, from[1] + normalY],
  ]]];
};

/**
 * Inserts a vertex into a ring, at the given position.
 *
 * Positions are ring indices rather than coordinates, so inserting into a shape that has two
 * identical vertices — which stored plans do have — affects the one that was clicked.
 */
export const insertVertex = (
  coordinates: MultiPolygonCoordinates,
  polygonIndex: number,
  ringIndex: number,
  afterVertex: number,
  vertex: Coordinate,
): MultiPolygonCoordinates => coordinates.map((polygon, p) => (
  p !== polygonIndex ? polygon : polygon.map((ring, r) => {
    if (r !== ringIndex) {
      return ring;
    }

    const next = [...ring];
    next.splice(afterVertex + 1, 0, vertex);
    return next;
  })
));

/**
 * Removes a vertex, unless doing so would leave something that is no longer a shape.
 *
 * A closed ring carries its first vertex twice, so the smallest real triangle is four
 * entries. Below that the ring has no area, and a feature with no area is invisible and
 * unselectable — an admin who removed one vertex too many would have deleted the shape
 * without being told.
 */
export const removeVertex = (
  coordinates: MultiPolygonCoordinates,
  polygonIndex: number,
  ringIndex: number,
  vertexIndex: number,
): MultiPolygonCoordinates | null => {
  const ring = coordinates[polygonIndex]?.[ringIndex];

  if (!ring || ring.length <= 4) {
    return null;
  }

  return coordinates.map((polygon, p) => (
    p !== polygonIndex ? polygon : polygon.map((candidate, r) => {
      if (r !== ringIndex) {
        return candidate;
      }

      const next = candidate.filter((_, index) => index !== vertexIndex);

      // Removing the first or last entry of a closed ring breaks the closure, so it is
      // re-closed rather than left as a shape with a gap in it.
      return closeRing(next.slice(0, next.length - (vertexIndex === 0 ? 1 : 0)));
    })
  ));
};

/** Moves one vertex, leaving every other entry identical. */
export const moveVertex = (
  coordinates: MultiPolygonCoordinates,
  polygonIndex: number,
  ringIndex: number,
  vertexIndex: number,
  to: Coordinate,
): MultiPolygonCoordinates => coordinates.map((polygon, p) => (
  p !== polygonIndex ? polygon : polygon.map((ring, r) => {
    if (r !== ringIndex) {
      return ring;
    }

    const next = ring.map((vertex, index) => (index === vertexIndex ? to : vertex));

    // A closed ring's first and last entries are the same point. Moving one has to move the
    // other, or the ring silently stops being closed.
    const isEndpoint = vertexIndex === 0 || vertexIndex === ring.length - 1;
    const wasClosed = ring.length > 1
      && ring[0][0] === ring[ring.length - 1][0]
      && ring[0][1] === ring[ring.length - 1][1];

    if (isEndpoint && wasClosed) {
      next[0] = to;
      next[next.length - 1] = to;
    }

    return next;
  })
));

/**
 * Pushes or pulls one side of a ring, keeping it parallel to where it was.
 *
 * This is what "extend a room" means to somebody who is not a draughtsman: grab the wall on the
 * side that needs to grow and pull it out. Only the part of the pointer's travel that is square
 * to the side counts — sliding along it would shear the shape, and a room that was rectangular
 * should stay rectangular.
 *
 * `edgeIndex` is the side's first vertex; the side runs to the next one. Both ends move by the
 * same offset, and `moveVertex` keeps a closed ring closed when either end is its first point.
 */
export const moveEdge = (
  coordinates: MultiPolygonCoordinates,
  polygonIndex: number,
  ringIndex: number,
  edgeIndex: number,
  deltaX: number,
  deltaY: number,
): MultiPolygonCoordinates => {
  const ring = coordinates[polygonIndex]?.[ringIndex];

  if (!ring || edgeIndex < 0 || edgeIndex >= ring.length - 1) {
    return coordinates;
  }

  const from = ring[edgeIndex];
  const to = ring[edgeIndex + 1];
  const length = distanceBetween(from, to);

  if (length <= 0) {
    return coordinates;
  }

  // The unit normal to the side, and how far along it the pointer has travelled.
  const normalX = -(to[1] - from[1]) / length;
  const normalY = (to[0] - from[0]) / length;
  const along = (deltaX * normalX) + (deltaY * normalY);

  if (along === 0) {
    return coordinates;
  }

  const offsetX = along * normalX;
  const offsetY = along * normalY;

  const moved = moveVertex(
    coordinates,
    polygonIndex,
    ringIndex,
    edgeIndex,
    [from[0] + offsetX, from[1] + offsetY],
  );

  return moveVertex(
    moved,
    polygonIndex,
    ringIndex,
    edgeIndex + 1,
    [to[0] + offsetX, to[1] + offsetY],
  );
};

/**
 * A point pulled straight across or straight up from another, whichever it is nearer to.
 *
 * What holding Shift, Ctrl or Alt does while drawing: the segment being aimed from the last
 * point stays level or upright, following whichever way the pointer has travelled further —
 * which is how a straight wall gets drawn with a hand that is not.
 */
export const straightenFrom = (anchor: Coordinate, point: Coordinate): Coordinate => (
  Math.abs(point[0] - anchor[0]) >= Math.abs(point[1] - anchor[1])
    ? [point[0], anchor[1]]
    : [anchor[0], point[1]]
);
